/**
 * Inicio contable (DISENO_CONTABILIDAD §7.1): lo que la directiva mira
 * primero. Cuatro indicadores, ingresos vs gastos, gastos por rubro y
 * evolución del disponible.
 */
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ArrowRight, Landmark, Scale, Users, Receipt } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import {
  Card,
  Cargando,
  ErrorCarga,
  PageHeader,
} from "../../components/ui";
import { hasPermission } from "../../lib/auth";
import { pesos, pesosCortos } from "../../lib/format";
import { useAuth } from "../auth/useAuth";
import { fetchResumen, type Resumen } from "./api";
import {
  AvisoProvisorio,
  BarraPeriodo,
  SinDatos,
  usePeriodoUrl,
} from "./componentes";
import {
  BarrasHorizontales,
  COLOR_GASTOS,
  COLOR_INGRESOS,
  EvolucionDisponible,
  IngresosGastos,
  Leyenda,
} from "./graficos";

function Indicador({
  icon: Icon,
  titulo,
  valor,
  tono,
  children,
}: {
  icon: LucideIcon;
  titulo: string;
  valor: number;
  tono?: "resultado" | undefined;
  children?: React.ReactNode;
}): JSX.Element {
  const color =
    tono === "resultado" ? (valor < 0 ? "text-rose-700" : "text-emerald-700") : "text-neutral-900";
  return (
    <Card padding="md" className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-sm text-neutral-600">
        <Icon className="h-4 w-4 text-brand-700" aria-hidden="true" />
        {titulo}
      </div>
      <p className={`text-2xl font-semibold tabular-nums ${color}`} title={pesos(valor)}>
        {pesosCortos(valor)}
      </p>
      {children && <div className="text-xs text-neutral-600">{children}</div>}
    </Card>
  );
}

function Indicadores({ r }: { r: Resumen }): JSX.Element {
  const ultimo = r.meses[r.meses.length - 1];
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <Indicador icon={Landmark} titulo="Disponible" valor={r.disponible}>
        <ul className="space-y-0.5">
          {r.disponible_detalle.map((c) => (
            <li key={c.cuenta_id} className="flex justify-between gap-2">
              <Link to={`/contabilidad/mayor/${c.cuenta_id}`} className="truncate hover:text-brand-700">
                {c.nombre}
              </Link>
              <span className="tabular-nums">{pesos(c.monto)}</span>
            </li>
          ))}
        </ul>
      </Indicador>
      <Indicador icon={Scale} titulo="Resultado acumulado" valor={r.resultado_acumulado} tono="resultado">
        {ultimo && (
          <>
            {ultimo.etiqueta}: <span className="tabular-nums">{pesos(r.resultado_mes)}</span>
            {ultimo.provisorio && " (provisorio)"}
            <br />
            Ingresos {pesos(r.ingresos_acumulados)} · gastos {pesos(r.gastos_acumulados)}
          </>
        )}
      </Indicador>
      <Indicador icon={Users} titulo="Deudores socios" valor={r.deudores_socios}>
        Consumos, cuotas, multas y convenios por cobrar.
      </Indicador>
      <Indicador icon={Receipt} titulo="Obligaciones por pagar" valor={r.obligaciones}>
        Proveedores, remuneraciones, cotizaciones e impuestos.
      </Indicador>
    </div>
  );
}

export function InicioContablePage(): JSX.Element {
  const { state } = useAuth();
  const url = usePeriodoUrl();
  const puedeCargar = hasPermission(state, "contabilidad.cargar");
  const q = useQuery({
    queryKey: ["contabilidad", "resumen", url.filtro],
    queryFn: () => fetchResumen(url.filtro),
    // Al cambiar el período se mantiene lo anterior en pantalla mientras carga.
    placeholderData: keepPreviousData,
  });
  const r = q.data;
  const comite = state?.empresaTargetNombre ?? state?.empresaNombre;

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="space-y-3">
        <PageHeader title="Resumen contable" subtitle={comite} />
        <BarraPeriodo periodo={r?.periodo} url={url} />
        <AvisoProvisorio periodo={r?.periodo} />
      </div>

      {q.isLoading && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {r && r.periodo.vacio && <SinDatos puedeCargar={puedeCargar} />}

      {r && !r.periodo.vacio && (
        <>
          {puedeCargar && r.cuentas_sin_clasificar > 0 && (
            <Link
              to="/contabilidad/plan?filtro=sin_clasificar"
              className="flex items-center justify-between rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100"
            >
              {r.cuentas_sin_clasificar === 1
                ? "1 cuenta con movimiento está sin clasificar: no entra en los indicadores."
                : `${r.cuentas_sin_clasificar} cuentas con movimiento están sin clasificar: no entran en los indicadores.`}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          )}

          <Indicadores r={r} />

          <Card padding="md" className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-medium text-neutral-900">Ingresos y gastos por mes</h2>
              <Leyenda
                items={[
                  { color: COLOR_INGRESOS, texto: "Ingresos" },
                  { color: COLOR_GASTOS, texto: "Gastos" },
                ]}
              />
            </div>
            <IngresosGastos meses={r.meses} />
            <details className="text-sm">
              <summary className="cursor-pointer text-brand-700 hover:text-brand-800">Ver tabla</summary>
              <table className="mt-2 w-full text-sm">
                <thead className="bg-neutral-100 text-xs uppercase tracking-wide text-neutral-600">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">Mes</th>
                    <th className="px-2 py-1.5 text-right font-medium">Ingresos</th>
                    <th className="px-2 py-1.5 text-right font-medium">Gastos</th>
                    <th className="px-2 py-1.5 text-right font-medium">Resultado</th>
                    <th className="px-2 py-1.5 text-right font-medium">Disponible</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {r.meses.map((m) => (
                    <tr key={`${m.anio}-${m.mes}`}>
                      <td className="px-2 py-1.5">
                        {m.etiqueta}
                        {m.provisorio && <span className="ml-1 text-xs text-amber-700">(provisorio)</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{pesos(m.ingresos)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{pesos(m.gastos)}</td>
                      <td
                        className={`px-2 py-1.5 text-right tabular-nums ${m.resultado < 0 ? "text-rose-600" : ""}`}
                      >
                        {pesos(m.resultado)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{pesos(m.disponible)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </Card>

          <div className="grid gap-5 lg:grid-cols-2">
            <Card padding="md" className="space-y-3">
              <h2 className="font-medium text-neutral-900">Gastos por rubro</h2>
              {r.gastos_por_rubro.length === 0 ? (
                <p className="text-sm text-neutral-600">Sin gastos en el período.</p>
              ) : (
                <>
                  <BarrasHorizontales
                    items={r.gastos_por_rubro.map((g) => ({
                      clave: g.rubro,
                      etiqueta: g.titulo,
                      valor: g.total,
                    }))}
                  />
                  <details className="text-sm">
                    <summary className="cursor-pointer text-brand-700 hover:text-brand-800">
                      Ver cuentas de cada rubro
                    </summary>
                    <div className="mt-2 space-y-3">
                      {r.gastos_por_rubro.map((g) => (
                        <div key={g.rubro}>
                          <p className="text-xs font-medium uppercase tracking-wide text-neutral-600">
                            {g.titulo}
                          </p>
                          <ul>
                            {g.cuentas.map((c) => (
                              <li key={c.cuenta_id} className="flex justify-between gap-2 py-0.5">
                                <Link
                                  to={`/contabilidad/mayor/${c.cuenta_id}`}
                                  className="truncate text-neutral-800 hover:text-brand-700"
                                >
                                  {c.nombre}
                                </Link>
                                <span className="tabular-nums">{pesos(c.monto)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </details>
                </>
              )}
            </Card>
            <Card padding="md" className="space-y-3">
              <h2 className="font-medium text-neutral-900">Disponible al cierre de cada mes</h2>
              <EvolucionDisponible meses={r.meses} />
              <p className="text-xs text-neutral-600">
                Caja, bancos, ahorro y fondos. Punto hueco = mes provisorio.
              </p>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
